import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthenticatedUser } from '@nexuscorp/shared-types'
import { clearTokens, setTokens } from '../lib/axios'
import { queryClient } from '../lib/queryClient'

interface AuthState {
  user: AuthenticatedUser | null
  isAuthenticated: boolean
  isLoading: boolean       // true pendant l'hydratation du store persisté
  _hasHydrated: boolean    // flag interne — hydratation terminée

  setUser: (user: AuthenticatedUser) => void
  setTokensAndUser: (accessToken: string, refreshToken: string, user: AuthenticatedUser) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setHasHydrated: (v: boolean) => void

  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,       // ← démarre à true jusqu'à hydratation
      _hasHydrated: false,

      setHasHydrated: (v) => set({ _hasHydrated: v, isLoading: false }),

      setUser: (user) => set({ user, isAuthenticated: true, isLoading: false }),

      setTokensAndUser: (accessToken, refreshToken, user) => {
        setTokens(accessToken, refreshToken)
        set({ user, isAuthenticated: true, isLoading: false })
      },

      logout: () => {
        clearTokens()
        queryClient.clear()
        set({ user: null, isAuthenticated: false, isLoading: false })
      },

      setLoading: (isLoading) => set({ isLoading }),

      hasPermission: (permission) => {
        const { user } = get()
        if (!user) return false
        const roles = user.roles ?? []
        const permissions = user.permissions ?? []
        if (roles.includes('SUPER_ADMIN')) return true
        return permissions.includes(permission)
      },

      hasRole: (role) => {
        const { user } = get()
        return user?.roles?.includes(role) ?? false
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get()
        return permissions.some(hasPermission)
      },

      hasAllPermissions: (permissions) => {
        const { hasPermission } = get()
        return permissions.every(hasPermission)
      },
    }),
    {
      name: 'nexuscorp-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // ← Appelé quand le store est réhydraté depuis localStorage
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    },
  ),
)
